import * as THREE from 'three';
import { resolveComponentInstanceTransformTarget } from '../../assembly/component-instance/ComponentInstanceTransformTargetResolver';
import { ImportedModelObjectMetadataService } from '../metadata';
import {
    isImportedTopologySelectionObject,
    resolveImportedTopologyHost,
} from './ImportedModelTopologyPickResolver';

/**
 * 导入模型选择对象解析器配置。
 */
export interface ImportedModelSelectionObjectResolverOptions {
    /** 当前场景。 */
    scene: THREE.Scene;
    /** 可选的对象 ID 查询函数。 */
    getSceneObjectById?: (objectId: string) => THREE.Object3D | null;
}

/**
 * 导入模型选择对象解析器。
 *
 * 设计说明：
 * 1. 统一收口 imported-model 的对象层级判定、line 命中归一化与 selection proxy 解析；
 * 2. 选择、高亮、拖拽三条链路都复用同一份“命中谁 -> 语义对象是谁”的规则；
 * 3. 装配体移动场景下，拖拽目标继续委托 component-instance 解析变换根，避免选择链和拖拽链优先级分裂。
 */
export class ImportedModelSelectionObjectResolver {
    /** 运行时依赖。 */
    private readonly options: ImportedModelSelectionObjectResolverOptions;

    /**
     * 创建导入模型选择对象解析器。
     *
     * @param options 运行时依赖。
     */
    public constructor(options: ImportedModelSelectionObjectResolverOptions) {
        this.options = options;
    }

    /**
     * 判断对象是否属于 imported-model 层级。
     *
     * @param object 目标对象。
     * @returns 是否属于导入模型。
     */
    public static isImportedModelObject(object: THREE.Object3D | null): boolean {
        let current = object;
        while (current) {
            if (ImportedModelObjectMetadataService.peekMetadata(current)) {
                return true;
            }
            current = current.parent;
        }
        return false;
    }

    /**
     * 把 imported-model line 命中归一到最近的非 line 语义宿主。
     *
     * @param object 输入对象。
     * @returns 归一化后的对象。
     */
    public static normalizeImportedModelHitObject(object: THREE.Object3D): THREE.Object3D {
        if (isImportedTopologySelectionObject(object)) {
            return object;
        }

        if (!(object instanceof THREE.Line) || object instanceof THREE.LineSegments) {
            return object;
        }

        let current: THREE.Object3D | null = object.parent;
        while (current) {
            if (
                ImportedModelSelectionObjectResolver.isImportedModelObject(current)
                && (!(current instanceof THREE.Line) || current instanceof THREE.LineSegments)
            ) {
                return current;
            }
            current = current.parent;
        }

        return object;
    }

    /**
     * 判断对象是否属于 imported-model 层级。
     *
     * @param object 目标对象。
     * @returns 是否属于导入模型。
     */
    public isImportedModelObject(object: THREE.Object3D | null): boolean {
        return ImportedModelSelectionObjectResolver.isImportedModelObject(object);
    }

    /**
     * 归一化 imported-model 命中对象。
     *
     * @param object 输入对象。
     * @returns 归一化后的对象。
     */
    public normalizeImportedModelHitObject(object: THREE.Object3D): THREE.Object3D {
        return ImportedModelSelectionObjectResolver.normalizeImportedModelHitObject(object);
    }

    /**
     * 将命中的 imported-model 子对象归一化为最终选择对象。
     *
     * @param sourceObject 原始命中对象。
     * @returns 最终选择对象；未命中 imported-model 时返回 `null`。
     */
    public resolveSelectionObject(sourceObject: THREE.Object3D): THREE.Object3D | null {
        const topologyHost = resolveImportedTopologyHost(sourceObject);
        if (topologyHost && topologyHost !== sourceObject) {
            return sourceObject;
        }

        let current: THREE.Object3D | null = sourceObject;
        while (current) {
            const proxyObjectId = ImportedModelObjectMetadataService.resolveSelectionProxyObjectId(current)?.trim() ?? '';
            if (proxyObjectId.length > 0) {
                return this.normalizeImportedModelHitObject(this.resolveSceneObjectById(proxyObjectId) ?? sourceObject);
            }

            const hierarchySelectionObjectId = ImportedModelObjectMetadataService
                .resolveHierarchySelectionObjectId(current)
                ?.trim() ?? '';
            if (hierarchySelectionObjectId.length > 0) {
                return this.normalizeImportedModelHitObject(this.resolveSceneObjectById(hierarchySelectionObjectId) ?? sourceObject);
            }

            if (ImportedModelObjectMetadataService.peekMetadata(current)) {
                return this.normalizeImportedModelHitObject(current);
            }

            current = current.parent;
        }
        return null;
    }

    /**
     * 解析装配体移动应使用的变换根对象。
     *
     * 设计说明：
     * 1. 拖拽目标必须先经过 imported-model selection proxy 规则归一；
     * 2. 变换根解析统一委托 component-instance 模块，避免选择链和拖拽链维护两套优先级；
     * 3. 点击导入 mesh 时，整组高亮与整组移动会指向同一对象。
     *
     * @param sourceObject 原始命中对象。
     * @returns 可移动的变换根对象；未命中 imported-model 时返回 `null`。
     */
    public resolveTransformRootObject(sourceObject: THREE.Object3D): THREE.Object3D | null {
        const selectionObject = this.resolveSelectionObject(sourceObject);
        if (!selectionObject) {
            return null;
        }

        const sourceTransformRoot = this.resolveComponentTransformTarget(sourceObject);
        if (sourceTransformRoot) {
            return sourceTransformRoot;
        }

        /**
         * 离线包保存重载后，可见 occurrence 可能仍携带指向 root 包重复 occurrence 的旧 selection proxy。
         * 该 proxy 已被组合层降级为资源保留子树时不能继续作为 transform 来源，但原始命中对象的父链仍代表
         * 当前可交互项目节点。这里先从原始命中对象进入组件 transform target 统一入口，只有失败后才用
         * selection object 兜底，避免旧代理抢占同源实例或绕过 bodyBinding 覆盖规则。
         */
        return selectionObject === sourceObject
            ? null
            : this.resolveComponentTransformTarget(selectionObject);
    }

    /**
     * 按组件实例统一规则解析可写 transform 根。
     *
     * @param object 候选命中对象或选择代理对象。
     * @returns 可写组件根；无法解析时返回 `null`。
     */
    private resolveComponentTransformTarget(object: THREE.Object3D): THREE.Object3D | null {
        return resolveComponentInstanceTransformTarget(
            object,
            {
                scene: this.options.scene,
                getSceneObjectById: (objectId) => this.resolveSceneObjectById(objectId),
            },
        );
    }

    /**
     * 根据对象 ID 查找场景对象。
     *
     * @param objectId 目标对象 ID。
     * @returns 命中的场景对象；未命中时返回 `null`。
     */
    private resolveSceneObjectById(objectId: string): THREE.Object3D | null {
        if (this.options.getSceneObjectById) {
            return this.options.getSceneObjectById(objectId);
        }

        let targetObject: THREE.Object3D | null = null;
        this.options.scene.traverse((object) => {
            if (!targetObject && object.uuid === objectId) {
                targetObject = object;
            }
        });
        return targetObject;
    }
}
